import React, { useState, memo } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  message,
} from "antd";
import { FaUser } from "react-icons/fa";
import { moment, getUserObj } from "../../../../../../../../utils";
import { contactResultOptions, reasonOptions, REASON_REQUIRED_RESULTS } from "../constants";
import SectionHeader from "./SectionHeader";

const { Option } = Select;
const { TextArea } = Input;

const initFormData = {
  result: undefined,
  reason: undefined,
  description: "",
};

/**
 * Section 2 — บันทึกการติดต่อใหม่
 *
 * @param {{
 *   isExpanded: boolean,
 *   onToggle: () => void,
 *   onSave: (entry: object) => void,
 * }} props
 */
const ContactForm = ({ isExpanded, onToggle, onSave, onDirtyChange }) => {
  const [formData, setFormData] = useState(initFormData);
  const [hasPurchase, setHasPurchase] = useState(false);
  const [hasNextAppointment, setHasNextAppointment] = useState(false);
  const [appointmentDateTime, setAppointmentDateTime] = useState(null);
  const [appointmentNote, setAppointmentNote] = useState("");

  const isReasonRequired = REASON_REQUIRED_RESULTS.has(formData.result);

  const isFormDirty =
    formData.result !== undefined ||
    formData.reason !== undefined ||
    Boolean(formData.description && formData.description.trim() !== "") ||
    hasPurchase ||
    hasNextAppointment ||
    appointmentDateTime !== null ||
    Boolean(appointmentNote && appointmentNote.trim() !== "");

  React.useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(isFormDirty);
    }
  }, [isFormDirty, onDirtyChange]);

  const handleClearForm = () => {
    setFormData(initFormData);
    setHasPurchase(false);
    setHasNextAppointment(false);
    setAppointmentDateTime(null);
    setAppointmentNote("");
  };

  const handleSave = () => {
    if (!formData.result) {
      message.error("กรุณาเลือกผลการติดต่อ");
      return;
    }
    if (isReasonRequired && !formData.reason) {
      message.error("กรุณาเลือกสาเหตุ");
      return;
    }
    if (hasNextAppointment && !appointmentDateTime) {
      message.error("กรุณาเลือกวันที่และเวลานัดหมายครั้งถัดไป");
      return;
    }

    const newEntry = {
      id: Date.now(),
      create_by: getUserObj()?.name || "-",
      contact_result: formData.result,
      contact_reason: isReasonRequired ? formData.reason : null,
      description: formData.description.trim(),
      is_purchased: hasPurchase,
      is_appointment: hasNextAppointment,
      appointment_datetime: appointmentDateTime
        ? appointmentDateTime.format("DD/MM/YYYY HH:mm")
        : null,
      appointment_note: appointmentNote,
    };

    onSave(newEntry);
    handleClearForm();
  };

  return (
    <>
      <SectionHeader
        title="บันทึกการติดต่อใหม่"
        isExpanded={isExpanded}
        onToggle={onToggle}
      />

      {isExpanded && (
        <Card
          size="small"
          className="mb-2"
          style={{ background: "#ffffff", borderColor: "#e5e7eb", borderRadius: "8px" }}
          bodyStyle={{ padding: "16px" }}
        >
          {/* Row 1: หัวข้อ | ผลการติดต่อ | สาเหตุ (conditional) */}
          <Row gutter={[12, 12]} className="mb-3">
            {/* <Col span={isReasonRequired ? 8 : 14}>
              <div
                style={{ fontSize: "11px", color: "#4b5563", fontWeight: "500", marginBottom: "4px" }}
              >
                หัวข้อ <span style={{ color: "#ef4444" }}>*</span>{" "}
                <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: "normal" }}>
                  Subject / Title
                </span>
              </div>
              <Input
                value={formData.title}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({ ...prev, title: val }));
                }}
                placeholder="ระบุหัวข้อการติดต่อ เช่น โทรติดตามโปรโมชั่น 30ct / ยืนยันคำสั่งซื้อ"
                style={{ borderRadius: "6px" }}
              />
            </Col> */}

            <Col span={isReasonRequired ? 12 : 24}>
              <div
                style={{ fontSize: "11px", color: "#4b5563", fontWeight: "500", marginBottom: "4px" }}
              >
                ผลการติดต่อ <span style={{ color: "#ef4444" }}>*</span>{" "}
                <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: "normal" }}>
                  Contact Result
                </span>
              </div>
              <Select
                value={formData.result}
                onChange={(val) =>
                  setFormData((prev) => ({ ...prev, result: val, reason: undefined }))
                }
                placeholder="— เลือกผลการติดต่อ —"
                style={{ width: "100%" }}
              >
                {contactResultOptions.map((opt) => (
                  <Option key={opt.value} value={opt.value}>
                    {opt.label}
                  </Option>
                ))}
              </Select>
            </Col>

            {isReasonRequired && (
              <Col span={12}>
                <div
                  style={{
                    fontSize: "11px",
                    color: "#4b5563",
                    fontWeight: "500",
                    marginBottom: "4px",
                  }}
                >
                  สาเหตุ <span style={{ color: "#ef4444" }}>*</span>{" "}
                  <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: "normal" }}>
                    Reason
                  </span>
                </div>
                <Select
                  value={formData.reason}
                  onChange={(val) => setFormData((prev) => ({ ...prev, reason: val }))}
                  placeholder="— เลือกสาเหตุ —"
                  style={{ width: "100%" }}
                >
                  {reasonOptions.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
          </Row>

          {/* Row 2: รายละเอียด */}
          <Row gutter={[12, 12]} className="mb-3">
            <Col span={24}>
              <div
                style={{ fontSize: "11px", color: "#4b5563", fontWeight: "500", marginBottom: "4px" }}
              >
                รายละเอียด{" "}
                <span style={{ fontSize: "10px", color: "#9ca3af", fontWeight: "normal" }}>
                  Remark / Details
                </span>
              </div>
              <TextArea
                value={formData.description}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormData((prev) => ({ ...prev, description: val }));
                }}
                placeholder="รายละเอียดจากการติดต่อล่าสุด เช่น ลูกค้าสนใจโปรโมชั่น 30ct / นัดโทรกลับพรุ่งนี้"
                rows={2}
                style={{ borderRadius: "6px" }}
              />
            </Col>
          </Row>

          {/* Row 3: Checkboxes */}
          <Row gutter={[12, 12]} className="mb-3">
            <Col span={24}>
              <div className="d-flex align-items-center" style={{ gap: "24px" }}>
                <Checkbox
                  checked={hasPurchase}
                  onChange={(e) => setHasPurchase(e.target.checked)}
                >
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#262626" }}>
                    มีการซื้อ
                  </span>
                </Checkbox>
                <Checkbox
                  checked={hasNextAppointment}
                  onChange={(e) => setHasNextAppointment(e.target.checked)}
                >
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#262626" }}>
                    นัดหมายครั้งถัดไป
                  </span>
                </Checkbox>
              </div>
            </Col>
          </Row>

          {/* Dashed sub-box: Next Appointment */}
          {hasNextAppointment && (
            <div
              className="mb-3"
              style={{
                border: "1px dashed #d9d9d9",
                borderRadius: "8px",
                background: "#fafafa",
                padding: "16px",
              }}
            >
              <div style={{ marginBottom: "14px" }}>
                <div
                  style={{ fontSize: "12px", color: "#262626", fontWeight: "bold", marginBottom: "6px" }}
                >
                  วันที่และเวลานัดหมายครั้งถัดไป
                </div>
                <DatePicker
                  showTime
                  value={appointmentDateTime}
                  onChange={setAppointmentDateTime}
                  format="DD/MM/YYYY HH:mm"
                  placeholder="เลือกวันที่และเวลา"
                  style={{ width: "100%", borderRadius: "6px" }}
                />
              </div>
              <div>
                <div
                  style={{ fontSize: "12px", color: "#262626", fontWeight: "bold", marginBottom: "6px" }}
                >
                  รายละเอียดการนัดหมาย
                </div>
                <TextArea
                  value={appointmentNote}
                  onChange={(e) => {
                    const val = e.target.value;
                    setAppointmentNote(val);
                  }}
                  placeholder="รายละเอียดเรื่องที่นัดหมาย"
                  rows={2}
                  style={{ borderRadius: "6px" }}
                />
              </div>
            </div>
          )}

          {/* Action Row */}
          <div className="d-flex justify-content-end align-items-center" style={{ paddingTop: "4px" }}>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <Button onClick={handleClearForm}>ล้างข้อมูล</Button>
              <Button type="primary" onClick={handleSave}>
                + บันทึกการติดต่อ
              </Button>
            </div>
          </div>
        </Card>
      )}
    </>
  );
};

export default memo(ContactForm);
